import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import styles from "./Messages.module.scss";
import { Search, MessageCircle, Plus, Send, Loader, ChevronLeft } from "lucide-react";
import { api } from "../../services/api";
import { useUser } from "../../context/UserContext";
import { useSocket } from "../../hooks/useSocket";
import { useMessages, Message } from "../../hooks/useMessages";
import Avatar from "../../components/Avatar/Avatar";
import StatusIndicator from "../../components/StatusIndicator/StatusIndicator";
import { useOnlineStatus } from "../../hooks/useOnlineStatus";
import EmptyState from "../../components/EmptyState";

interface Conversation {
  conversationId: string;
  participants: string[];
  lastMessageAt: string;
  otherParticipant: { userId: string; username: string };
  unreadCount: number;
}

export default function MessagesPage() {
  const { user } = useUser();
  const { subscribe, emit } = useSocket();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Conversation list state
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [convoLoading, setConvoLoading] = useState(true);
  const [convoError, setConvoError] = useState<string | null>(null);

  // Search
  const [query, setQuery] = useState("");

  // Active conversation
  const [activeId, setActiveId] = useState<string | null>(null);

  // New conversation dialog
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [newSearchQuery, setNewSearchQuery] = useState("");
  const [newConvoLoading, setNewConvoLoading] = useState(false);
  const [newConvoError, setNewConvoError] = useState<string | null>(null);
  const [contactsList, setContactsList] = useState<{ userId: string; username: string }[]>([]);
  const [contactsLoading, setContactsLoading] = useState(false);

  // Message input
  const [messageBody, setMessageBody] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);

  // Typing indicator
  const [typingUsers, setTypingUsers] = useState<Record<string, boolean>>({});
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastTypingEmitRef = useRef<number>(0);

  // Messages hook
  const { messages, loading: messagesLoading, hasMore, loadMore, sendMessage } = useMessages(activeId);

  // Scroll ref for chat body
  const chatBodyRef = useRef<HTMLDivElement>(null);

  // Online status
  const { fetchStatuses } = useOnlineStatus();

  // Load conversations on mount
  // Load conversations on mount
  useEffect(() => {
    loadConversations();
  }, []);

  const loadConversations = async () => {
    setConvoLoading(true);
    setConvoError(null);
    try {
      const response = await api.messaging.listConversations();
      setConversations(response.items);

      // Auto-open conversation from URL param (e.g. from notification click)
      const convoParam = searchParams.get("conversation");
      if (convoParam) {
        setActiveId(convoParam);
        // Clear the param from URL so it doesn't re-trigger
        searchParams.delete("conversation");
        setSearchParams(searchParams, { replace: true });
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to load conversations";
      setConvoError(msg);
    } finally {
      setConvoLoading(false);
    }
  };

  // Filter conversations by search query
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return conversations;
    return conversations.filter((c) =>
      c.otherParticipant.username.toLowerCase().includes(q)
    );
  }, [query, conversations]);

  // Fetch online statuses for all conversation participants
  useEffect(() => {
    if (conversations.length === 0) return;
    const participantIds = conversations.map((c) => c.otherParticipant.userId);
    fetchStatuses(participantIds);
  }, [conversations, fetchStatuses]);

  const activeConversation = conversations.find((c) => c.conversationId === activeId) ?? null;

  // Mark conversation as read when selecting it
  const handleSelectConversation = useCallback(
    async (conversationId: string) => {
      setActiveId(conversationId);
      // Reset typing state for new conversation
      setTypingUsers({});

      // Mark as read
      try {
        await api.messaging.markRead(conversationId);
        // Update unread count locally
        setConversations((prev) =>
          prev.map((c) =>
            c.conversationId === conversationId ? { ...c, unreadCount: 0 } : c
          )
        );
      } catch {
        // Silently ignore mark-read failures
      }
    },
    []
  );

  // Handle sending a message
  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    const body = messageBody.trim();
    if (!body || !activeId) return;

    setSendingMessage(true);
    try {
      await sendMessage(body);
      setMessageBody("");
      // Update conversation lastMessageAt and move to top
      setConversations((prev) => {
        const updated = prev.map((c) =>
          c.conversationId === activeId
            ? { ...c, lastMessageAt: new Date().toISOString() }
            : c
        );
        // Sort by lastMessageAt descending
        return updated.sort(
          (a, b) => new Date(b.lastMessageAt).getTime() - new Date(a.lastMessageAt).getTime()
        );
      });
    } catch {
      // Error handled in useMessages
    } finally {
      setSendingMessage(false);
    }
  };

  // New conversation flow — load contacts (followers + following) when dialog opens
  useEffect(() => {
    if (!showNewDialog || !user) return;
    let cancelled = false;

    const loadContacts = async () => {
      setContactsLoading(true);
      try {
        const [followersRes, followingRes] = await Promise.all([
          api.social.getFollowers(user.userId),
          api.social.getFollowing(user.userId),
        ]);
        if (cancelled) return;

        // Deduplicate by userId
        const allUsers = [...(followersRes.users || []), ...(followingRes.users || [])];
        const seen = new Set<string>();
        const unique = allUsers.filter((u) => {
          if (seen.has(u.userId)) return false;
          seen.add(u.userId);
          return true;
        });
        setContactsList(unique);
      } catch {
        setContactsList([]);
      } finally {
        if (!cancelled) setContactsLoading(false);
      }
    };

    loadContacts();
    return () => { cancelled = true; };
  }, [showNewDialog, user]);

  // Filtered contacts based on search
  const filteredContacts = useMemo(() => {
    const q = newSearchQuery.trim().toLowerCase();
    if (!q) return contactsList;
    return contactsList.filter((c) => c.username.toLowerCase().includes(q));
  }, [newSearchQuery, contactsList]);

  // Start conversation with a selected user
  const handleSelectContact = async (targetUserId: string) => {
    setNewConvoLoading(true);
    setNewConvoError(null);
    try {
      const convo = await api.messaging.createConversation(targetUserId);
      // Find the contact's username from our contacts list
      const contact = contactsList.find((c) => c.userId === targetUserId);
      const conversationItem: Conversation = {
        conversationId: convo.conversationId,
        participants: convo.participants,
        lastMessageAt: convo.lastMessageAt || convo.createdAt || new Date().toISOString(),
        otherParticipant: convo.otherParticipant || {
          userId: targetUserId,
          username: contact?.username || "User",
        },
        unreadCount: 0,
      };
      setConversations((prev) => {
        const exists = prev.find((c) => c.conversationId === conversationItem.conversationId);
        if (exists) return prev;
        return [conversationItem, ...prev];
      });
      setActiveId(conversationItem.conversationId);
      setShowNewDialog(false);
      setNewSearchQuery("");
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to create conversation";
      setNewConvoError(msg);
    } finally {
      setNewConvoLoading(false);
    }
  };

  // Subscribe to new_message for updating conversation list
  useEffect(() => {
    const unsub = subscribe("new_message", (msg: Message) => {
      // Ignore messages sent by the current user
      if (msg.senderId === user?.userId) return;

      setConversations((prev) => {
        const updated = prev.map((c) => {
          if (c.conversationId === msg.conversationId) {
            return {
              ...c,
              lastMessageAt: msg.createdAt,
              // Increment unread if not the active conversation
              unreadCount:
                msg.conversationId === activeId ? c.unreadCount : c.unreadCount + 1,
            };
          }
          return c;
        });
        return updated.sort(
          (a, b) => new Date(b.lastMessageAt).getTime() - new Date(a.lastMessageAt).getTime()
        );
      });
    });
    return unsub;
  }, [subscribe, activeId, user?.userId]);

  // Typing indicator: subscribe to incoming typing events
  useEffect(() => {
    const unsub = subscribe("typing", (data: { conversationId: string; userId: string }) => {
      if (data.conversationId !== activeId) return;
      if (data.userId === user?.userId) return;

      setTypingUsers((prev) => ({ ...prev, [data.userId]: true }));

      // Clear typing indicator after 3 seconds
      setTimeout(() => {
        setTypingUsers((prev) => {
          const next = { ...prev };
          delete next[data.userId];
          return next;
        });
      }, 3000);
    });
    return unsub;
  }, [subscribe, activeId, user?.userId]);

  // Emit typing event on keystroke (debounced)
  const handleMessageInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMessageBody(e.target.value);

    if (!activeId) return;
    const now = Date.now();
    // Debounce: emit at most once per 2 seconds
    if (now - lastTypingEmitRef.current > 2000) {
      emit("typing", { conversationId: activeId });
      lastTypingEmitRef.current = now;
    }
  };

  // Auto-scroll to bottom only if user is already near the bottom
  const isTyping = Object.keys(typingUsers).length > 0;

  useEffect(() => {
    const el = chatBodyRef.current;
    if (!el) return;
    // Only scroll if within 150px of the bottom
    const isNearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 150;
    if (isNearBottom) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages, isTyping]);

  return (
    <div className={styles.page}>
      <div className={styles.shell}>
        {/* Sidebar — hidden on mobile when chat is open */}
        <aside className={`${styles.sidebar} ${activeId ? styles.sidebarHidden : ""}`}>
          <header className={styles.sidebarHeader}>
            <h1 className={styles.title}>Messages</h1>

            <button
              type="button"
              className={styles.newBtn}
              title="New message"
              onClick={() => setShowNewDialog(true)}
            >
              <Plus size={16} />
            </button>
          </header>

          {/* New conversation dialog — search by username from followers/following */}
          {showNewDialog && (
            <div style={{ padding: "0 12px 8px", position: "relative" }}>
              <div className={styles.search} style={{ marginBottom: 0 }}>
                <Search size={16} />
                <input
                  className={styles.searchInput}
                  placeholder="Search by username…"
                  value={newSearchQuery}
                  onChange={(e) => setNewSearchQuery(e.target.value)}
                  autoFocus
                />
                <button
                  type="button"
                  className={styles.newBtn}
                  onClick={() => { setShowNewDialog(false); setNewSearchQuery(""); setNewConvoError(null); }}
                  title="Cancel"
                  style={{ transform: "rotate(45deg)" }}
                >
                  <Plus size={14} />
                </button>
              </div>
              {newConvoError && (
                <div style={{ color: "var(--color-text-error)", fontSize: "0.75rem", padding: "4px 0" }}>
                  {newConvoError}
                </div>
              )}
              <div style={{
                maxHeight: 200,
                overflowY: "auto",
                background: "var(--color-bg-input)",
                borderRadius: 8,
                marginTop: 4,
                border: "1px solid var(--color-border)",
              }}>
                {contactsLoading ? (
                  <div style={{ padding: "12px", color: "var(--color-text-muted)", fontSize: "0.85rem", textAlign: "center" }}>
                    Loading contacts…
                  </div>
                ) : filteredContacts.length === 0 ? (
                  <div style={{ padding: "12px", color: "var(--color-text-muted)", fontSize: "0.85rem", textAlign: "center" }}>
                    {newSearchQuery ? "No users found" : "No followers or following"}
                  </div>
                ) : (
                  filteredContacts.map((contact) => (
                    <button
                      key={contact.userId}
                      type="button"
                      onClick={() => handleSelectContact(contact.userId)}
                      disabled={newConvoLoading}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        width: "100%",
                        padding: "10px 12px",
                        background: "transparent",
                        border: "none",
                        borderBottom: "1px solid var(--color-border)",
                        color: "var(--color-text)",
                        cursor: "pointer",
                        fontSize: "0.9rem",
                        textAlign: "left",
                      }}
                    >
                      <div style={{
                        width: 32,
                        height: 32,
                        borderRadius: "50%",
                        background: "var(--color-bg-active)",
                        display: "grid",
                        placeItems: "center",
                        fontSize: "0.8rem",
                        fontWeight: 600,
                        flexShrink: 0,
                      }}>
                        {contact.username[0]?.toUpperCase()}
                      </div>
                      <span>{contact.username}</span>
                    </button>
                  ))
                )}
              </div>
            </div>
          )}

          <div className={styles.search}>
            <Search size={16} />
            <input
              className={styles.searchInput}
              placeholder="Search messages…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>

          <div className={styles.list}>
            {convoLoading ? (
              <div className={styles.emptyList}>Loading…</div>
            ) : convoError ? (
              <div className={styles.emptyList}>{convoError}</div>
            ) : filtered.length === 0 ? (
              <div className={styles.emptyList}>No conversations found</div>
            ) : (
              filtered.map((c) => (
                <button
                  key={c.conversationId}
                  type="button"
                  className={`${styles.item} ${
                    c.conversationId === activeId ? styles.itemActive : ""
                  }`}
                  onClick={() => handleSelectConversation(c.conversationId)}
                >
                  <div
                    className={styles.avatar}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/profile?user=${c.otherParticipant.userId}`);
                    }}
                    style={{ cursor: "pointer" }}
                    title={`View ${c.otherParticipant.username}'s profile`}
                  >
                    <Avatar
                      src={(c.otherParticipant as any).profileImageUrl || (c.otherParticipant as any).avatarUrl}
                      username={c.otherParticipant.username}
                      size="md"
                    />
                    <StatusIndicator userId={c.otherParticipant.userId} variant="dot" />
                  </div>

                  <div className={styles.itemBody}>
                    <div className={styles.itemTop}>
                      <div className={styles.name}>{c.otherParticipant.username}</div>
                      <div className={styles.time}>
                        {formatTime(c.lastMessageAt)}
                      </div>
                    </div>

                    <div className={styles.preview}>
                      {c.conversationId === activeId && isTyping
                        ? "typing…"
                        : ""}
                    </div>
                  </div>

                  {c.unreadCount > 0 && (
                    <div className={styles.unreadDot} />
                  )}
                </button>
              ))
            )}
          </div>
        </aside>

        {/* Main panel — hidden on mobile when no chat is active */}
        <main className={`${styles.main} ${!activeId ? styles.mainHidden : ""}`}>
          {!activeConversation ? (
            <EmptyState
              icon={<MessageCircle size={24} />}
              heading="Select a conversation"
              description="Choose a chat to start messaging"
            />
          ) : (
            <div className={styles.placeholderChat}>
              <div className={styles.chatHeader}>
                <button
                  type="button"
                  className={styles.backBtn}
                  onClick={() => setActiveId(null)}
                  title="Back to conversations"
                >
                  <ChevronLeft size={18} />
                </button>
                <span
                  onClick={() => navigate(`/profile?user=${activeConversation.otherParticipant.userId}`)}
                  style={{ cursor: "pointer" }}
                  title={`View ${activeConversation.otherParticipant.username}'s profile`}
                >
                  {activeConversation.otherParticipant.username}
                </span>
                {isTyping && (
                  <span style={{ fontSize: "0.8rem", fontWeight: 400, marginLeft: 8, color: "var(--color-text-muted)" }}>
                    typing…
                  </span>
                )}
              </div>

              <div className={styles.chatBody} ref={chatBodyRef}>
                {messagesLoading && messages.length === 0 ? (
                  <div style={{ color: "var(--color-text-muted)" }}>Loading messages…</div>
                ) : (
                  <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 8, padding: "12px 0" }}>
                    {hasMore && (
                      <button
                        type="button"
                        onClick={loadMore}
                        disabled={messagesLoading}
                        style={{
                          alignSelf: "center",
                          padding: "6px 14px",
                          borderRadius: 8,
                          border: "1px solid var(--color-border)",
                          background: "var(--color-bg-input)",
                          color: "var(--color-text-muted)",
                          cursor: "pointer",
                          fontSize: "0.85rem",
                        }}
                      >
                        {messagesLoading ? "Loading…" : "Load older messages"}
                      </button>
                    )}
                    {messages.map((msg) => (
                      <MessageBubble
                        key={msg.messageId}
                        message={msg}
                        isMine={msg.senderId === user?.userId}
                      />
                    ))}
                    {isTyping && <TypingIndicator />}
                  </div>
                )}
              </div>

              {/* Send message form */}
              <form
                onSubmit={handleSend}
                style={{
                  display: "flex",
                  gap: 8,
                  padding: "10px 0 0",
                  borderTop: "1px solid var(--color-border)",
                }}
              >
                <input
                  type="text"
                  value={messageBody}
                  onChange={handleMessageInput}
                  placeholder="Type a message…"
                  style={{
                    flex: 1,
                    padding: "10px 14px",
                    borderRadius: 12,
                    border: "1px solid var(--color-border-input)",
                    background: "var(--color-bg-input)",
                    color: "var(--color-text)",
                    fontSize: "16px",
                    outline: "none",
                  }}
                  disabled={sendingMessage}
                />
                <button
                  type="submit"
                  disabled={sendingMessage || !messageBody.trim()}
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 12,
                    border: "1px solid var(--color-border)",
                    background: messageBody.trim()
                      ? "var(--color-button-bg)"
                      : "var(--color-bg-input)",
                    color: messageBody.trim() ? "var(--color-button-text)" : "var(--color-text-muted)",
                    cursor: messageBody.trim() ? "pointer" : "default",
                    display: "grid",
                    placeItems: "center",
                  }}
                  title="Send"
                >
                  <Send size={16} />
                </button>
              </form>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

/** Simple message bubble component */
function MessageBubble({ message, isMine }: { message: Message; isMine: boolean }) {
  const isDeleted = !!message.deletedAt;

  return (
    <div
      style={{
        alignSelf: isMine ? "flex-end" : "flex-start",
        maxWidth: "75%",
        padding: "8px 12px",
        borderRadius: 12,
        background: isMine
          ? "color-mix(in srgb, var(--color-accent) 25%, var(--color-bg-card))"
          : "var(--color-bg-input)",
        border: "1px solid var(--color-border)",
      }}
    >
      {isDeleted ? (
        <span style={{ fontStyle: "italic", color: "var(--color-text-muted)", fontSize: "0.9rem" }}>
          This message was deleted
        </span>
      ) : (
        <span style={{ color: "var(--color-text)", fontSize: "0.95rem", wordBreak: "break-word" }}>
          {message.body}
        </span>
      )}
      <div style={{ fontSize: "0.7rem", color: "var(--color-text-muted)", marginTop: 4, textAlign: "right" }}>
        {formatTime(message.createdAt)}
      </div>
    </div>
  );
}

/** Animated typing indicator bubble (3 bouncing dots) */
function TypingIndicator() {
  return (
    <div
      style={{
        alignSelf: "flex-start",
        padding: "10px 14px",
        borderRadius: 12,
        background: "var(--color-bg-input)",
        border: "1px solid var(--color-border)",
        display: "flex",
        gap: 4,
        alignItems: "center",
      }}
    >
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          style={{
            width: 7,
            height: 7,
            borderRadius: "50%",
            background: "var(--color-text-muted, rgba(255,255,255,0.5))",
            animation: `typingBounce 1.4s infinite ease-in-out`,
            animationDelay: `${i * 0.2}s`,
          }}
        />
      ))}
      <style>{`
        @keyframes typingBounce {
          0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
          30% { transform: translateY(-4px); opacity: 1; }
        }
      `}</style>
    </div>
  );
}

/** Format a date string to a relative time */
function formatTime(dateStr: string): string {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);

  if (diffMin < 1) return "Just now";
  if (diffMin < 60) return `${diffMin}m`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h`;
  const diffD = Math.floor(diffH / 24);
  if (diffD === 1) return "Yesterday";
  if (diffD < 7) return `${diffD}d`;
  return date.toLocaleDateString();
}
